import { App } from "@/constants";
import { CURRENT_APP_QUERY_PARAMETER } from "@/constants/current_app";
import { globalLoggerToken, gwStorefrontAppInfoToken } from "@/tokens";
import { reviewsApplicationLoggerToken } from "@apps/reviews/tokens";
import { wishlistAuthApiClientToken } from "@apps/wishlist/tokens";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { MinilogLogger } from "@lib/Logger";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { BaseModule } from "@modules/base_module/BaseModule";
import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
import { CommonModule } from "@modules/common_module/CommonModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import { assert } from "superstruct";
import { container, inject, instanceCachingFactory, singleton } from "tsyringe";

import { ApplicationInterface } from "@interfaces/ApplicationInterface";

import DesignFeedHomeWidgetInjector from "./design_feed/widgets/design_feed_home_widget/DesignFeedHomeWidgetInjector";
import DesignFeedPageWidgetInjector from "./design_feed/widgets/design_feed_page_widget/DesignFeedPageWidgetInjector";
import DesignFeedProductWidgetInjector from "./design_feed/widgets/design_feed_product_widget/DesignFeedProductWidgetInjector";
import {
    IloomCustomizationApplicationConfigInterface,
    IloomCustomizationApplicationConfigSchema,
    IloomCustomizationApplicationConfigToken,
} from "./IloomCustomizationApplicationConfig";
import ReviewsPageWidgetInjector from "./reviews/widgets/reviews_page_widget/IloomReviewsPageWidgetInjector";
import {
    iloomCustomizationApplicationLoggerToken,
    iloomCustomizationAuthApiClientToken,
    iloomReviewsAuthApiClientToken,
} from "./tokens";
import WishlistPageWidgetInjector from "./wishlist/widgets/wishlist_page_widget/WishlistPageWidgetInjector";

@singleton()
export class IloomCustomizationApplication
    implements ApplicationInterface<object>
{
    widgets = [];
    exports = {};
    constructor(
        @inject(IloomCustomizationApplicationConfigToken)
        public readonly config: IloomCustomizationApplicationConfigInterface,
        @inject(CommonModule)
        public readonly commonModule: CommonModule,
        @inject(BaseModule)
        public readonly baseModule: BaseModule,
        @inject(MutationObserverInjector)
        private readonly mutationObserverInjector: MutationObserverInjector
    ) {
        assert(config, IloomCustomizationApplicationConfigSchema);
    }
    init(): this {
        const analyticEventsCollectorModule = container.resolve(
            AnalyticsEventsCollectorModule
        );
        const eventsCollector =
            analyticEventsCollectorModule.exports.eventsCollector;
        const analyticsEventsFactory =
            analyticEventsCollectorModule.exports.eventsFactory;
        const mediaManagerLogger = new MinilogLogger("mediaManager");
        container.registerInstance(
            iloomCustomizationApplicationLoggerToken,
            mediaManagerLogger
        );
        const reviewsApplicationLogger = new MinilogLogger("reviews");
        container.registerInstance(
            reviewsApplicationLoggerToken,
            reviewsApplicationLogger
        );

        container.register(iloomCustomizationAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(globalLoggerToken)
                        ),
                        eventsCollector,
                        this.commonModule.exports.locationController,
                        analyticsEventsFactory
                    ),
                    di.resolve(TokenManagerModule).exports.tokenManager
                );
            }),
        });

        container.register(iloomReviewsAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/reviews-storefront/v2"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            mediaManagerLogger
                        ),
                        eventsCollector,
                        this.commonModule.exports.locationController,
                        analyticsEventsFactory
                    ),
                    di.resolve(TokenManagerModule).exports.tokenManager
                );
            }),
        });

        container.register(wishlistAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/wishlist-storefront/v2"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(globalLoggerToken)
                        ),
                        eventsCollector,
                        this.commonModule.exports.locationController,
                        analyticsEventsFactory
                    ),
                    di.resolve(TokenManagerModule).exports.tokenManager
                );
            }),
        });

        mediaManagerLogger.debug("MediaManagerApplication is initialized", {
            ...this.config,
        });

        const designFeedPageWidgetInjector = new DesignFeedPageWidgetInjector(
            this.config.designFeedPageWidgetPlaceholderSelector,
            mediaManagerLogger,
            this.mutationObserverInjector
        );
        const designFeedHomeWidgetInjector = new DesignFeedHomeWidgetInjector(
            this.config.designFeedHomeWidgetPlaceholderSelector,
            mediaManagerLogger,
            this.mutationObserverInjector
        );
        const designFeedProductWidgetInjector =
            new DesignFeedProductWidgetInjector(
                this.config.designFeedProductWidgetPlaceholderSelector,
                mediaManagerLogger,
                this.mutationObserverInjector
            );
        const wishlistPageWidgetInjector = new WishlistPageWidgetInjector(
            this.config.wishlistPageWidgetPlaceholderSelector,
            mediaManagerLogger,
            this.mutationObserverInjector
        );
        const reviewsPageWidgetInjector = new ReviewsPageWidgetInjector(
            this.config.reviewsPageWidgetPlaceholderSelector,
            mediaManagerLogger,
            this.mutationObserverInjector
        );

        designFeedPageWidgetInjector.start();
        designFeedHomeWidgetInjector.start();
        designFeedProductWidgetInjector.start();
        wishlistPageWidgetInjector.start();
        reviewsPageWidgetInjector.start();

        this.baseModule.exports.modulesStatusesProvider.updateModuleStatus(
            App.IloomCustomization,
            MODULE_STATUS.INITIALIZED
        );

        return this;
    }
}
